<script setup>
/**
 * ImmersiveSwitchButton
 * Quick "switch to immersive" entry shown inside the regular catalog. Same
 * action + lightning icon + label in both placements; `variant` only changes the
 * styling ('header' = premium pill on the desktop header, 'footer' = on the dark
 * mobile cart footer). Context-aware: on the agent catalog it targets the agent
 * immersive route, keeping the clientMakat param + full query (agentOverride)
 * so the on-behalf context survives the round trip.
 */
import { useRoute, useRouter } from 'vue-router'
import { ROUTES } from '../../../core/config/constants.js'

defineProps({
  variant: {
    type: String,
    default: 'header',
    validator: (v) => v === 'header' || v === 'footer',
  },
})

const route = useRoute()
const router = useRouter()

function goImmersive() {
  if (route.name === ROUTES.AGENTS_CATALOG) {
    router.push({
      name: ROUTES.AGENTS_CATALOG_IMMERSIVE,
      params: { clientMakat: route.params.clientMakat },
      query: route.query,
    })
  } else {
    router.push({ name: ROUTES.CATALOG_IMMERSIVE })
  }
}
</script>

<template>
  <button
    type="button"
    class="imm-switch"
    :class="`imm-switch--${variant}`"
    aria-label="עבור לקטלוג אימרסיבי"
    @click="goImmersive"
  >
    <svg class="imm-switch-bolt" viewBox="0 0 24 24" aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="currentColor" />
    </svg>
    <span>עבור לאימרסיב</span>
  </button>
</template>

<style scoped>
.imm-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

/* Premium gold bolt on both variants. */
.imm-switch-bolt {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #fbbf24;
  filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.55));
}

/* Header (desktop) — premium brand pill on the light app bar. */
.imm-switch--header {
  padding: 8px 16px;
  border-radius: var(--border-radius-full);
  background: var(--gradient-primary);
  color: #ffffff;
  font-size: var(--font-size-sm);
  box-shadow: 0 2px 8px rgba(30, 41, 59, 0.25);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.imm-switch--header:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.35);
}

.imm-switch--header:active {
  transform: translateY(0) scale(0.98);
}

/* Footer (mobile) — sits on the dark gradient cart footer. */
.imm-switch--footer {
  padding: 8px 14px;
  border-radius: var(--border-radius-lg);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: var(--font-size-sm);
}

.imm-switch--footer:active {
  background: rgba(255, 255, 255, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .imm-switch--header {
    transition: none;
  }
}
</style>
